import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useGroceryList } from './useGroceryList'
import { useGenerateGroceryList } from './useGenerateGroceryList'
import { useUserRole } from '@/shared/hooks/useUserRole'
import { useWakeLock } from './useWakeLock'
import { useTickGroceryItem } from './useTickGroceryItem'
import { CategorySection } from './CategorySection'
import { Dialog } from '@/shared/components/Dialog'
import { useGroceryListRealtime } from './useGroceryListRealtime'
import { useOfflineSync } from './useOfflineSync'
import { GroceryListSyncBanner } from './GroceryListSyncBanner'
import { AddAdHocItemForm } from './AddAdHocItemForm'
import { GroceryProgress } from './GroceryProgress'
import { PantryExclusionDisclosure } from './PantryExclusionDisclosure'
import { AlreadyGotSection } from './AlreadyGotSection'
import { useClearAlreadyGot } from './useClearAlreadyGot'
import type { GroceryListItem } from './types'

const AISLE_ORDER = [
  'Produce',
  'Meat & Fish',
  'Dairy',
  'Bakery',
  'Tinned & Dry Goods',
  'Household',
  'Other',
] as const

export function GroceryListView() {
  const { t } = useTranslation('grocery-list')
  const { data, isLoading, isError } = useGroceryList()
  const { mutate: generate, isPending, isError: isGenerateError } = useGenerateGroceryList()
  const role = useUserRole()
  const isPlanner = role === 'Planner'
  const { mutate: clearAlreadyGot } = useClearAlreadyGot()

  useWakeLock()
  useGroceryListRealtime()
  useOfflineSync()
  const { handleTick } = useTickGroceryItem()

  const [regenerateDialogOpen, setRegenerateDialogOpen] = useState(false)

  const hasList = (data?.items?.length ?? 0) > 0

  const grouped = useMemo(() => {
    const map = new Map<string, GroceryListItem[]>()
    for (const item of (data?.items ?? [])) {
      const bucket = map.get(item.aisleCategory) ?? []
      bucket.push(item)
      map.set(item.aisleCategory, bucket)
    }
    return map
  }, [data?.items])

  const total = data?.items?.length ?? 0
  const ticked = data?.items?.filter(i => i.ticked).length ?? 0

  if (isLoading) {
    return (
      <div aria-label={t('loadingAriaLabel')} role="status">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="h-10 bg-gray-100 rounded animate-pulse mb-2" />
        ))}
      </div>
    )
  }

  if (isError) {
    return <p role="alert">{t('errorMessage')}</p>
  }

  return (
    <div className="flex flex-col h-full">
      <GroceryListSyncBanner />
      <div className="flex flex-col gap-4 p-4 overflow-y-auto">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">{t('title')}</h1>
        {isPlanner && (
          <>
            {!hasList ? (
              <button
                type="button"
                onClick={() => generate()}
                disabled={isPending}
                aria-label={t('generateButtonAriaLabel')}
                className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
              >
                {isPending ? t('generating') : t('generate')}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setRegenerateDialogOpen(true)}
                disabled={isPending}
                aria-label={t('regenerate.button')}
                className="rounded-md border border-line px-4 py-2 text-sm font-medium text-ink disabled:opacity-50"
              >
                {isPending ? t('generating') : t('regenerate.button')}
              </button>
            )}
          </>
        )}
      </div>

      {hasList && <GroceryProgress total={total} ticked={ticked} />}

      {hasList && (
        <PantryExclusionDisclosure
          count={data?.excludedPantryCount ?? 0}
          names={data?.excludedPantryNames ?? []}
        />
      )}

      {isGenerateError && (
        <p role="alert" className="text-sm text-destructive">{t('generateError')}</p>
      )}

      {!hasList ? (
        <div
          role="status"
          aria-label={t('emptyStateAriaLabel')}
          className="flex flex-col items-center gap-3 py-16 text-center text-muted-foreground"
        >
          <p className="text-lg">{t('emptyStateTitle')}</p>
          <p className="text-sm">{t('emptyStateDescription')}</p>
        </div>
      ) : (
        <div
          aria-label={t('listAriaLabel')}
          className="overflow-y-auto"
        >
          {AISLE_ORDER
            .filter(aisle => (grouped.get(aisle)?.length ?? 0) > 0)
            .map(aisle => (
              <CategorySection
                key={aisle}
                category={t(`aisleCategory.${aisle}`)}
                items={grouped.get(aisle)!}
                onTick={handleTick}
              />
            ))
          }
          {[...grouped.keys()]
            .filter(key => !(AISLE_ORDER as readonly string[]).includes(key))
            .map(key => (
              <CategorySection
                key={key}
                category={key}
                items={grouped.get(key)!}
                onTick={handleTick}
              />
            ))
          }
        </div>
      )}

      <AddAdHocItemForm />

      {(data?.alreadyGotItems?.length ?? 0) > 0 && (
        <AlreadyGotSection
          items={data!.alreadyGotItems}
          onClear={() => clearAlreadyGot()}
        />
      )}

      <Dialog
        open={regenerateDialogOpen}
        onClose={() => setRegenerateDialogOpen(false)}
        title={t('regenerate.dialogTitle')}
      >
        <p className="text-sm text-muted mb-6">{t('regenerate.dialogBody')}</p>
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => setRegenerateDialogOpen(false)}
            aria-label={t('regenerate.cancel')}
            className="rounded-md border border-line px-4 py-2 text-sm font-medium text-ink"
          >
            {t('regenerate.cancel')}
          </button>
          <button
            type="button"
            onClick={() => {
              generate()
              setRegenerateDialogOpen(false)
            }}
            aria-label={t('regenerate.confirm')}
            className="rounded-md bg-accent px-4 py-2 text-sm font-medium text-canvas"
          >
            {t('regenerate.confirm')}
          </button>
        </div>
      </Dialog>
      </div>
    </div>
  )
}
